/**
 * What a Todoist sync does to a mirrored project's status, and which mirrored
 * projects it may delete.
 *
 * A project is Active or Archived, and for a mirrored project Todoist decides
 * which: archived there → Archived here, otherwise Active. The older stages
 * (planning, hold, complete) are rewritten to Active.
 *
 * Todoist's /projects lists only active projects; archived ones come from
 * /projects/archived. A project in neither list was deleted in Todoist. If the
 * archived list could not be fetched, nothing is deleted that run: an archived
 * project would look deleted, and deleting a project takes its sections, its
 * tasks (completed history included) and its link from logged time with it.
 *
 * Pure: imports types only, so tests/project-status-sync.test.mjs loads it directly.
 */

import type { Project, ProjectStatus } from "./types";

/** The status to write, or undefined to leave the stored one alone. */
export function mirroredStatus(current: ProjectStatus | undefined, archivedInTodoist: boolean): ProjectStatus | undefined {
  const next: ProjectStatus = archivedInTodoist ? "archived" : "active";
  return current === next ? undefined : next;
}

/**
 * Splits mirrored projects that are not active in Todoist into those to delete
 * and those to keep untouched. `archivedIds` is null when the archived list
 * could not be fetched.
 *
 * `kept` holds Project Hub ids: their sections and tasks are absent from
 * Todoist's active lists too, and must not be deleted as gone.
 */
export function missingProjects<P extends Pick<Project, "id" | "todoistId">>(
  existing: P[],
  activeIds: Set<string>,
  archivedIds: Set<string> | null,
): { deleted: P[]; kept: Set<string> } {
  const deleted: P[] = [];
  const kept = new Set<string>();
  for (const p of existing) {
    if (activeIds.has(p.todoistId!)) continue;
    if (archivedIds === null || archivedIds.has(p.todoistId!)) kept.add(p.id);
    else deleted.push(p);
  }
  return { deleted, kept };
}

/**
 * Whether the status can be changed here. A mirrored project's follows
 * Todoist, so it is archived and unarchived there; only a project of Project
 * Hub's own (from before projects had to start in Todoist) is set here.
 */
export function statusEditable(project: Pick<Project, "todoistId"> | null | undefined): boolean {
  return !project?.todoistId;
}
